"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, ShieldCheck, Gauge, ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
interface GalleryItem {
  id: string;
  tag: string;
  title: string;
  dutyCycle: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  alt: string;
}
const galleryItems: GalleryItem[] = [{
  id: "cnc-mill",
  tag: "CNC-01 // MILLING",
  title: "High-speed 4-axis milling on 7075 aluminum stock",
  dutyCycle: "100% duty cycle rating",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_cnc_milling.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_cnc_milling.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_cnc_milling.webp-wvc-sizes",
  alt: "CNC machining center cutting aluminum alloy with flood coolant"
}, {
  id: "tig-weld",
  tag: "WLD-04 // FABRICATION",
  title: "Precision pulse TIG welding on chromoly tube chassis",
  dutyCycle: "220A continuous output",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_tig_welding.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_tig_welding.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_tig_welding.png-wvc-sizes",
  alt: "TIG welder laying clean bead on tubular steel frame"
}, {
  id: "metrology",
  tag: "MET-02 // QUALITY CHECK",
  title: "Surface plate inspection with 0.0001-inch dial indicators",
  dutyCycle: "Grade A certified precision",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_metrology_inspection.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_metrology_inspection.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_metrology_inspection.png-wvc-sizes",
  alt: "Machinist measuring precision machined component on surface plate"
}, {
  id: "hydraulic-press",
  tag: "PRS-09 // FORMING",
  title: "50-ton hydraulic press forming structural bracket joints",
  dutyCycle: "Reinforced H-frame",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_hydraulic_press.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_hydraulic_press.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_hydraulic_press.webp-wvc-sizes",
  alt: "Heavy duty hydraulic press stamping heavy gauge plate steel"
}, {
  id: "lathe-turning",
  tag: "LTH-03 // TURNING",
  title: "Heavy turning center profiling hardened steel shafts",
  dutyCycle: "Rigid cast iron bed",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_lathe_turning.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_lathe_turning.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_lathe_turning.webp-wvc-sizes",
  alt: "Industrial metal lathe turning solid steel shaft with chips flying"
}, {
  id: "torque-assembly",
  tag: "ASY-07 // ASSEMBLY",
  title: "Digital torque calibration on heavy vehicle hub assemblies",
  dutyCycle: "±1% torque accuracy",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_torque_assembly.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_torque_assembly.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1787497/img/workshop_torque_assembly.png-wvc-sizes",
  alt: "Technician tightening suspension bolts with calibrated torque wrench"
}];
export default function Workshop() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 14
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.24,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="workshop-in-action" data-nav="dark" className="bg-background text-foreground relative py-16 md:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-12 border-b border-border mb-10">
          <div className="max-w-2xl">
            {/* Precision Spec Rule flanking header */}
            <div className="flex items-center gap-2 mb-3">
              <span className="h-2 w-2 bg-primary rounded-xs" />
              <p className="font-mono text-xs md:text-sm font-semibold tracking-wider text-primary uppercase">
                Field Deployment // Live Ops
              </p>
            </div>

            <div className="flex items-stretch gap-4">
              <div className="w-[3px] bg-primary rounded-xs shrink-0" />
              <div>
                <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground uppercase leading-none">
                  Workshop in action
                </h2>
                <p className="mt-3 font-sans text-sm sm:text-base text-muted-foreground leading-relaxed">
                  See our machinery and maintenance equipment operating under sustained continuous load in fabrication, machining, and inspection bays.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Stat Tags */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 bg-card border border-border px-3 py-2 rounded-xs">
              <ShieldCheck className="size-4 text-primary shrink-0" />
              <span className="font-mono text-xs text-muted-foreground uppercase font-medium">
                Tested to ASTM specs
              </span>
            </div>
            <div className="flex items-center gap-2 bg-card border border-border px-3 py-2 rounded-xs">
              <Gauge className="size-4 text-primary shrink-0" />
              <span className="font-mono text-xs text-muted-foreground uppercase font-medium">
                24/7 Duty cycles
              </span>
            </div>
          </div>
        </div>

        {/* Masonry / Grid Container */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleryItems.map((item, index) => <motion.div key={item.id} data-index={index} variants={itemVariants} className="group flex flex-col bg-card border border-border rounded-xs overflow-hidden transition-[border-color,box-shadow] duration-200 hover:border-primary/80 hover:shadow-lg">
              {/* Image Container with strict Aspect Ratio & Zoom */}
              <div className="relative aspect-16/10 sm:aspect-4/3 w-full overflow-hidden bg-muted">
                <img src={item.imageSrc} data-wvc-srcset={item.imageSrcset} data-wvc-sizes={item.imageSizes} alt={item.alt} loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-300 ease-out group-hover:scale-[1.03]" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-200" />
                
                {/* Top Badge */}
                <div className="absolute top-3 left-3 z-10">
                  <Badge variant="outline" className="bg-background/90 backdrop-blur-xs border-border font-mono text-[11px] font-semibold text-foreground px-2 py-0.5 rounded-xs">
                    {item.tag}
                  </Badge>
                </div>
              </div>

              {/* Card Meta Body */}
              <div className="flex flex-1 flex-col justify-between p-5">
                <div>
                  <h3 className="font-serif text-lg font-bold uppercase tracking-tight text-card-foreground group-hover:text-primary transition-colors duration-150">
                    {item.title}
                  </h3>
                </div>

                <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground font-mono">
                  <div className="flex items-center gap-1.5">
                    <Wrench className="size-3.5 text-primary" />
                    <span>{item.dutyCycle}</span>
                  </div>
                  <span className="text-primary/90 uppercase tracking-wider font-semibold flex items-center gap-0.5">
                    Live bay
                    <ArrowUpRight className="size-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Footer Technical Bar */}
        <div className="mt-12 p-6 bg-card border border-border rounded-xs flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="h-2 w-2 bg-primary rounded-full animate-pulse shrink-0" />
            <p className="font-mono text-xs sm:text-sm text-foreground">
              Equipment available for commercial order and nationwide delivery.
            </p>
          </div>
          <Link to="/ShopPage" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-serif text-sm font-bold uppercase tracking-wider px-6 py-2.5 rounded-xs hover:bg-primary/90 transition-[background-color,transform] active:translate-y-0.5">
            Explore shop catalog
            <ArrowUpRight className="size-4" />
          </Link>
        </div>
      </div>
    </section>;
}